/**
 * TemplateVariablesCard — read-only reference card showing all available
 * email template variables. Used in both the System Owner Email Templates tab
 * and the Tenant Admin Email tab.
 *
 * Props:
 *   tenantLabel  — label for the TENANT column (default "Tenant")
 *   collapsible  — if true, renders with a toggle chevron (default false)
 *   defaultOpen  — initial open state when collapsible (default true)
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronDown, ChevronRight } from 'lucide-react';

const VAR = ({ v }) => (
  <code className="block bg-muted px-2 py-1 rounded">{v}</code>
);

const Col = ({ title, vars, note }) => (
  <div>
    <p className="font-semibold text-muted-foreground uppercase tracking-wide mb-1 text-[11px]">{title}</p>
    <div className="space-y-1">
      {vars.map(v => <VAR key={v} v={v} />)}
    </div>
    {note && <p className="text-[10px] text-muted-foreground mt-1">{note}</p>}
  </div>
);

const TemplateVariablesCard = ({ tenantLabel = 'Tenant', collapsible = false, defaultOpen = true, customVariables = [], customManagement = null }) => {
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);

  return (
    <Card>
      <CardHeader
        className={`py-3 px-6 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Template Variables</CardTitle>
          {collapsible && (
            open
              ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
              : <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      {open && (
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">

          <Col title="User" vars={['{user_name}', '{user_first_name}', '{user_email}', '{temp_password}']} />

          <Col
            title={tenantLabel}
            vars={['{tenant_name}', '{tenant_billing_email}', '{tenant_phone_number}', '{tenant_logo}', '{total_horses_sold_ytd}', '{total_net_proceeds_ytd}']}
          />

          <Col
            title="Invoice"
            vars={['{invoice_number}', '{billing_period_start}', '{billing_period_end}', '{invoice_due_amount}', '{invoice_summary_due_amount}']}
          />

          <Col title="Sale" vars={['{sale_full_name}', '{link}']} />

          <Col title="Report" vars={['{report_name}', '{horse_system_name}']} />

          {/* Account — standard + person-specific fields */}
          <div>
            <p className="font-semibold text-muted-foreground uppercase tracking-wide mb-1 text-[11px]">Account</p>
            <div className="space-y-1">
              {['{account_name}', '{access_key}', '{account_primary_email}', '{account_primary_phone}', '{account_primary_address}', '{account_salutation}'].map(v => (
                <VAR key={v} v={v} />
              ))}
              <p className="text-[10px] text-muted-foreground pt-1">Person accounts only:</p>
              {['{account_first_name}', '{account_last_name}', '{account_suffix}'].map(v => (
                <VAR key={v} v={v} />
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Address format: Street / City, State ZIP
            </p>
          </div>

          <Col
            title="Deliverable"
            vars={['{responsible_party}', '{deliverable_type}', '{deadline}']}
          />

          {/* Dynamic Tables */}
          <div>
            <p className="font-semibold text-muted-foreground uppercase tracking-wide mb-1 text-[11px]">Dynamic Tables</p>
            <div className="space-y-1">
              <code className="block bg-amber-100 px-2 py-1 rounded">{'{hip_number_table}'}</code>
              <code className="block bg-amber-100 px-2 py-1 rounded">{'{ship_in_table}'}</code>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Auto-generated HTML tables based on the owner's horses at the sale.</p>
          </div>

          {/* Date — auto-injected in every email */}
          <div>
            <p className="font-semibold text-muted-foreground uppercase tracking-wide mb-1 text-[11px]">Date</p>
            <div className="space-y-1">
              <code className="block bg-green-50 border border-green-200 px-2 py-1 rounded">{'{current_date}'}</code>
              <code className="block bg-green-50 border border-green-200 px-2 py-1 rounded">{'{current_datetime}'}</code>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Auto-injected in every email using your browser's local time.</p>
          </div>

        </div>

        {/* Custom variables added by System Owner */}
        {(customVariables.length > 0 || customManagement) && (
          <div className="border-t pt-3">
            <p className="font-semibold text-muted-foreground uppercase tracking-wide mb-2 text-[11px]">Custom</p>
            {customVariables.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
                {customVariables.map(v => (
                  <div key={v.key} className="flex items-center gap-1.5">
                    <code className="bg-muted px-2 py-1 rounded text-xs">{`{${v.key}}`}</code>
                    <span className="text-[10px] text-muted-foreground">{v.label}</span>
                    {v.field_path && <span className="text-[10px] text-blue-500">({v.field_path})</span>}
                    {v.static_value && !v.field_path && <span className="text-[10px] text-green-600">= "{v.static_value}"</span>}
                  </div>
                ))}
              </div>
            )}
            {customManagement}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default TemplateVariablesCard;
